import axios from "axios";
import { Toast } from "vant"; // 假设使用Element UI，可替换为其他UI库
import { removeToken, getNonce, generateSign } from "@/utils/auth";
// import { globalStore } from "@/stores/login";
import Vue from "vue";
import showToast from "@/components/basic/parts/components/toast/toast";
const errorMap = {
  "zh-cn": "网络出小差了，请稍后重试~",
  "en-us": "Network issue. Please try again later",
  "ja-jp": "エラーが発生しました。時間を空けてから再度お試しください",
  "ko-kr": "네트워크 오류가 발생했습니다, 잠시 후에 다시 시도해 주세요",
  "zh-tw": "網路較差，請稍後重試～",
  "fr-fr": "Problème de réseau. Réessayez plus tard.",
  "es-es": "Problema de red. Por favor, prueba más tarde",
  "de-de": "Netzwerkproblem. Bitte versuche es später erneut.",
};

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // 从环境变量获取基础URL
  timeout: 100000, // 超时时间设置
  headers: {
    "Content-Type": "application/json;charset=utf-8",
  },
});

// 存储请求取消令牌
const cancelTokens = new Map();

/**
 * 生成请求唯一标识
 * @param {Object} config 请求配置
 * @returns {String} 唯一标识
 */
function generateRequestKey(config) {
  const { method, url, params, data } = config;
  return [
    method.toUpperCase(),
    url,
    JSON.stringify(params),
    JSON.stringify(data),
  ].join("&");
}

/**
 * 添加请求取消令牌
 * @param {Object} config 请求配置
 */
function addCancelToken(config) {
  const requestKey = generateRequestKey(config);

  // 如果存在相同请求，取消之前的
  if (cancelTokens.has(requestKey)) {
    const cancel = cancelTokens.get(requestKey);
    cancelTokens.delete(requestKey);
  }

  // 创建新的取消令牌
  config.cancelToken = new axios.CancelToken((cancel) => {
    cancelTokens.set(requestKey, cancel);
  });
}

/**
 * 移除请求取消令牌
 * @param {Object} config 请求配置
 */
function removeCancelToken(config) {
  const requestKey = generateRequestKey(config);
  if (cancelTokens.has(requestKey)) {
    cancelTokens.delete(requestKey);
  }
}

// 请求拦截器
service.interceptors.request.use(
  async (config) => {
    // 添加取消令牌
    addCancelToken(config);

    const nonce = getNonce();
    const timestamp = Math.floor(Date.now() / 1000);
    const sign = await generateSign(nonce, timestamp, config.data);

    // config.headers[
    //   "Authorization"
    // ] = `Bearer ${globalStore.loginInfo?.accessToken}`;
    config.headers["Nonce"] = nonce;
    config.headers["Timestamp"] = timestamp;
    config.headers["Sign"] = sign;
    config.headers["Language"] = Vue.prototype.$lang;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    // 移除取消令牌
    removeCancelToken(response.config);
    // 隐藏加载状态
    // store.dispatch("app/hideLoading");

    const { data, config } = response;

    // 处理二进制响应（如文件下载）
    if (config.responseType === "blob" || config.url.includes(".oss")) {
      return data;
    }

    // 自定义业务状态码处理
    const { code, message, result } = data;

    // 成功状态码（根据实际业务调整）
    if (code === 0) {
      return result !== undefined ? result : data;
    }

    if (code !== 4001) {
      // 业务错误处理
      Toast({
        message: message || "Error",
        icon: "",
      });
    }

    return Promise.reject(data);
  },
  (error) => {
    // 移除取消令牌（如果有配置）
    if (error.config) {
      removeCancelToken(error.config);
    }

    // 隐藏加载状态
    // store.dispatch("app/hideLoading");

    // 取消请求不视为错误
    if (axios.isCancel(error)) {
      return Promise.resolve();
    }

    if (error.config?.ignoreError) {
      return Promise.resolve();
    }

    let errorMessage = errorMap[Vue.prototype.$lang];
    // 网络错误处理
    if (error.response) {
      const { status, data } = error.response;

      // HTTP状态码处理
      switch (status) {
        case 401:
          // 清除token并跳转登录页
          removeToken();
          // store.commit("user/SET_TOKEN", "");
          break;
        case 403:
          break;
        case 404:
          break;
        case 500:
          break;
        default:
          errorMessage = data?.message || errorMessage;
      }
    }
    showToast(errorMessage);
    return Promise.reject(error);
  },
);

/**
 * 封装请求方法
 * @param {String} method 请求方法
 * @param {String} url 请求URL
 * @param {Object} data 请求数据
 * @param {Object} options 额外配置
 * @returns {Promise}
 */
const request = (method, url, data = {}, options = {}) => {
  const config = {
    method,
    url,
    ...options,
  };

  // 根据请求方法设置数据参数
  if (method.toLowerCase() === "get") {
    config.params = data;
  } else {
    config.data = data;
  }

  return service(config);
};

// 导出常用请求方法
export default {
  get: (url, params, options) => request("get", url, params, options),
  post: (url, data, options) => request("post", url, data, options),
  put: (url, data, options) => request("put", url, data, options),
  delete: (url, data, options) => request("delete", url, data, options),
  // 上传文件
  upload: (url, formData, options = {}) => {
    return request("post", url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      ...options,
    });
  },
  // 下载文件
  download: (url, params, options = {}) => {
    return request("get", url, params, {
      responseType: "blob",
      ...options,
    }).then((blob) => {
      // 处理文件下载逻辑
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement("a");
      a.href = url;
      // 从响应头获取文件名
      const contentDisposition =
        options.response?.headers?.["content-disposition"];
      let fileName = "download";
      if (contentDisposition) {
        fileName = decodeURIComponent(
          contentDisposition.split("filename=")[1]?.replace(/"/g, "") ||
            fileName,
        );
      }
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return fileName;
    });
  },
};
